
interface FinancialValues {
  subtotal: number;
  discount: number;
  taxAmount: number;
  total: number;
  servicesTotal: number;
  vendorsTotal: number;
}

interface QuotationFinancialsProps {
  discountType: 'fixed' | 'percentage';
  discountValue: string;
  tax: string;
  isTaxable: boolean;
  validUntil: string;
  calculatedValues: FinancialValues;
  errors: Record<string, string>;
  onDiscountTypeChange: (value: 'fixed' | 'percentage') => void;
  onDiscountValueChange: (value: string) => void;
  onTaxChange: (value: string) => void;
  onIsTaxableChange: (value: boolean) => void;
  onValidUntilChange: (value: string) => void;
}

const INRIcon = ({ className }: { className?: string }) => (
  <span className={className}>₹</span>
);

// Get quotation status based on valid_until date
const getQuotationStatus = (validUntil: string): { status: string; color: string; daysLeft: number } => {
  if (!validUntil) return { status: "No expiry set", color: "text-stone-500", daysLeft: Infinity };

  const now = new Date();
  const expiryDate = new Date(validUntil);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const expiry = new Date(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate());

  const daysRemaining = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (daysRemaining < 0) {
    return { status: "Expired", color: "text-rose-600", daysLeft: daysRemaining };
  } else if (daysRemaining === 0) {
    return { status: "Expires today", color: "text-amber-600", daysLeft: 0 };
  } else if (daysRemaining === 1) {
    return { status: "Expires tomorrow", color: "text-amber-500", daysLeft: 1 };
  } else if (daysRemaining <= 7) {
    return { status: `${daysRemaining} days left`, color: "text-amber-500", daysLeft: daysRemaining };
  } else {
    return { status: `${daysRemaining} days left`, color: "text-emerald-600", daysLeft: daysRemaining };
  }
};

export default function QuotationFinancials({
  discountType,
  discountValue,
  tax,
  isTaxable,
  validUntil,
  calculatedValues,
  errors,
  onDiscountTypeChange,
  onDiscountValueChange,
  onTaxChange,
  onIsTaxableChange,
  onValidUntilChange,
}: QuotationFinancialsProps) {
  const quotationStatus = getQuotationStatus(validUntil);

  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4 sm:p-5">
      <h3 className="text-sm font-semibold text-stone-700 mb-4 flex items-center gap-2">
        <span className="w-5 h-5 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-bold">4</span>
        Financial Details
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
        {/* Discount Type */}
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1.5">Discount Type</label>
          <select
            value={discountType}
            onChange={(e) => onDiscountTypeChange(e.target.value as 'fixed' | 'percentage')}
            className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none bg-white"
          >
            <option value="fixed">Fixed Amount (₹)</option>
            <option value="percentage">Percentage (%)</option>
          </select>
        </div>

        {/* Discount Value */}
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1.5">
            Discount {discountType === 'percentage' ? '(%)' : '(₹)'}
          </label>
          <div className="relative">
            {discountType === 'fixed' && <INRIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 font-bold" />}
            <input
              type="number"
              min="0"
              step={discountType === 'percentage' ? "1" : "0.01"}
              max={discountType === 'percentage' ? "100" : undefined}
              value={discountValue}
              onChange={(e) => onDiscountValueChange(e.target.value)}
              placeholder="0"
              className={`w-full ${discountType === 'fixed' ? 'pl-8' : 'pl-3'} pr-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none`}
            />
          </div>
        </div>

        {/* Valid Until */}
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1.5">Valid Until *</label>
          <input
            type="date"
            value={validUntil}
            onChange={(e) => onValidUntilChange(e.target.value)}
            className={`w-full px-3 py-2 rounded-lg border text-sm transition-colors outline-none ${
              errors.valid_until
                ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
                : "border-stone-200 bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
            }`}
          />
          {errors.valid_until && <p className="text-xs text-rose-500 mt-1">{errors.valid_until}</p>}
        </div>

        {/* Tax — checkbox label + editable input grouped together */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs font-medium text-stone-600">Tax (%)</label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={isTaxable}
                onChange={(e) => onIsTaxableChange(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-stone-300 text-amber-500 focus:ring-amber-500 accent-amber-500"
              />
              <span className="text-[11px] text-stone-500 select-none">Apply tax</span>
            </label>
          </div>
          <input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={tax}
            onChange={(e) => onTaxChange(e.target.value)}
            placeholder="0"
            disabled={!isTaxable}
            className={`w-full px-3 py-2 rounded-lg border text-sm outline-none transition-colors ${
              !isTaxable
                ? "bg-stone-100 border-stone-200 text-stone-400 cursor-not-allowed"
                : "border-stone-200 bg-white text-stone-800 focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
            }`}
          />
        </div>

        {/* Validity Status Badge */}
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-stone-600 mb-1.5">Validity Status</label>
          <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${
            quotationStatus.status === 'Expired'
              ? 'bg-rose-50 border-rose-200 text-rose-600'
              : quotationStatus.status === 'No expiry set'
              ? 'bg-stone-50 border-stone-200 text-stone-500'
              : quotationStatus.status.includes('today') || quotationStatus.status.includes('tomorrow') || (quotationStatus.daysLeft !== undefined && quotationStatus.daysLeft <= 7)
              ? 'bg-amber-50 border-amber-200 text-amber-600'
              : 'bg-emerald-50 border-emerald-200 text-emerald-600'
          }`}>
            <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
            {quotationStatus.status}
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-gradient-to-br from-stone-50 to-stone-100 rounded-lg p-4 border border-stone-200">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs sm:text-sm mb-3">
          <div>
            <p className="text-stone-500 text-xs">Services</p>
            <p className="font-semibold text-stone-700">₹{Math.round(calculatedValues.servicesTotal).toLocaleString('en-IN')}</p>
          </div>
          <div>
            <p className="text-stone-500 text-xs">Vendors</p>
            <p className="font-semibold text-stone-700">₹{Math.round(calculatedValues.vendorsTotal).toLocaleString('en-IN')}</p>
          </div>
          <div>
            <p className="text-stone-500 text-xs">Subtotal</p>
            <p className="font-semibold text-stone-700">₹{Math.round(calculatedValues.subtotal).toLocaleString('en-IN')}</p>
          </div>
          <div>
            <p className="text-stone-500 text-xs">Discount</p>
            <p className="font-semibold text-rose-600">-₹{Math.round(calculatedValues.discount).toLocaleString('en-IN')}</p>
          </div>
        </div>
        <div className="border-t border-stone-300 pt-3 grid grid-cols-2 gap-3">
          <div>
            <p className="text-stone-500 text-xs">
              Tax {isTaxable ? `(${tax}%)` : '(Not Applicable)'}
            </p>
            <p className={`font-semibold ${isTaxable ? 'text-stone-700' : 'text-stone-400'}`}>
              ₹{Math.round(calculatedValues.taxAmount).toLocaleString('en-IN')}
            </p>
          </div>
          <div className="bg-white rounded-lg p-2 border border-amber-200">
            <p className="text-stone-500 text-xs">Total</p>
            <p className="font-bold text-amber-700 text-base sm:text-lg">₹{Math.round(calculatedValues.total).toLocaleString('en-IN')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
